import { Folder } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useFolders } from "@/hooks/folders";

interface FolderSelectorProps {
  value?: string | null;
  onValueChange: (value: string | null | undefined) => void;
  placeholder?: string;
  showAll?: boolean;
  showUncategorized?: boolean;
  className?: string;
}

export const FolderSelector = ({
  value,
  onValueChange,
  placeholder = "Select folder",
  showAll = true,
  showUncategorized = true,
  className,
}: FolderSelectorProps) => {
  const { folders, isLoading } = useFolders();

  const handleValueChange = (selectedValue: string) => {
    if (selectedValue === "all") {
      onValueChange(undefined);
    } else if (selectedValue === "none") {
      onValueChange(null);
    } else {
      onValueChange(selectedValue);
    }
  };

  const getDisplayValue = () => {
    if (value === undefined) return "all";
    if (value === null) return "none";
    return value;
  };

  return (
    <Select value={getDisplayValue()} onValueChange={handleValueChange} disabled={isLoading}>
      <SelectTrigger className={className}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {showAll && (
          <SelectItem value="all">
            <div className="flex items-center gap-2">
              <Folder className="h-4 w-4 text-gray-500" />
              <span>All Documents</span>
            </div>
          </SelectItem>
        )}
        {showUncategorized && (
          <SelectItem value="none">
            <div className="flex items-center gap-2">
              <Folder className="h-4 w-4 text-gray-400" />
              <span>Uncategorized</span>
            </div>
          </SelectItem>
        )}
        {folders.map((folder) => (
          <SelectItem key={folder.id} value={folder.id}>
            <div className="flex items-center gap-2">
              <Folder className="h-4 w-4" style={{ color: folder.color }} />
              <span>{folder.name}</span>
              {folder.document_count !== undefined && folder.document_count > 0 && (
                <span className="text-xs text-gray-500">({folder.document_count})</span>
              )}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
